import { getToken } from "@/utils/auth";
import {
  debugConsole,
  getWithExpiry,
  isDevelop,
  setWithExpiry,
} from "@/utils/helper";
import axios from "axios";
import _ from "lodash";
import type {
  FetchAPIRequest,
  FetchAPIResponse,
  FetchAPIWithLocalStorageRequest,
  FetchSenateAPIRequest,
} from "./type";

const VITE_SENATE_URL = import.meta.env.VITE_SENATE_URL;

async function fetchAPI({
  method,
  url,
  request,
  debug = "fetchAPI",
  auth = true,
  contentType = "application/json",
}: FetchAPIRequest): Promise<FetchAPIResponse> {
  try {
    const token = getToken();
    if (auth && !token) throw "Not found token!";

    const options = !_.isEmpty(request)
      ? method === "get"
        ? { params: { ...request } }
        : { data: { ...request } }
      : {};

    const response = await axios({
      method,
      url,
      headers: {
        "Content-Type": contentType,
        Authorization: `Bearer ${token}`,
      },
      maxBodyLength: Infinity,
      maxContentLength: Infinity,
      ...options,
    });

    return response;
  } catch (error: any) {
    debugConsole(error, debug);
    if (typeof error === "string") {
      return { status: 401, statusText: error, data: null };
    }
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      return { status, statusText: data.message, data: null };
    }
    return error;
  }
}

async function fetchSenateAPI({
  method,
  pathname,
  request = {},
  debug = "fetchSenateAPIDefault",
  auth = true,
  contentType = "application/json",
}: FetchSenateAPIRequest): Promise<FetchAPIResponse> {
  const url = new URL(VITE_SENATE_URL);
  url.pathname += `api/${pathname}`;

  const { status, statusText, data } = await fetchAPI({
    method,
    url: url.href,
    request,
    debug,
    auth,
    contentType,
  });

  if ((status === 200 || status === 201) && !_.isEmpty(data)) {
    const currentResponse = {
      status,
      statusText: data.message,
      data: data.data,
    };
    return currentResponse;
  }

  return { status, statusText, data: null };
}

async function fetchSenateAPIWithLocalStorage({
  method,
  pathname,
  request = {},
  debug = "fetchSenateAPIWithLocalStorage",
  auth = true,
  key,
  expiry = isDevelop ? 480 : 120, // 60 = 1 hour
  contentType = "application/json",
}: FetchAPIWithLocalStorageRequest): Promise<FetchAPIResponse> {
  const value = getWithExpiry(key);
  if (value && (value.status === 200 || value.status === 201)) {
    return value;
  } else {
    const response = await fetchSenateAPI({
      method,
      pathname,
      request,
      debug,
      auth,
      contentType,
    });

    if (
      (response.status === 200 || response.status === 201) &&
      !_.isEmpty(response.data)
    ) {
      setWithExpiry(key, response, expiry * 60000);
    }

    return response;
  }
}

export { fetchAPI, fetchSenateAPI, fetchSenateAPIWithLocalStorage };
